/**
 * The tour's step-jump index (opened by the "STEP N OF M" counter) — references/ui-spec.md §3.
 * A grouped, searchable list of the tour's steps; Enter jumps to the first match of the LIVE
 * query (the deferred list would land on the previous keystroke's result).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Input } from '@fluentui/react-components';
import { Search16Regular } from '@fluentui/react-icons';
import { HELP_PANEL_ATTR } from './dom';
import { fmt, HELP_STRINGS } from './strings';
import { HELP_TABSTER_ATTRS } from './tabster';
import { groupStepsBySection, localizedSection, localizedStep, stepMatchesQuery, type HelpLanguage, type HelpStep, type PageHelp } from './types';
import { useDeferredValue } from './useDeferred';

const WIDTH = 340;
const EDGE = 12;

interface HelpStepJumpProps {
  anchor: HTMLElement | null;
  help: PageHelp;
  /** The tour's steps, in tour order. */
  steps: HelpStep[];
  currentIndex: number;
  language: HelpLanguage;
  onPick: (index: number) => void;
  onClose: () => void;
}

export const HelpStepJump = ({ anchor, help, steps, currentIndex, language, onPick, onClose }: HelpStepJumpProps) => {
  const s = HELP_STRINGS[language];
  const [query, setQuery] = useState('');
  const deferred = useDeferredValue(query);
  const ref = useRef<HTMLDivElement>(null);

  const matching = useMemo(() => steps.filter((st) => stepMatchesQuery(st, deferred.trim())), [steps, deferred]);
  const groups = useMemo(() => groupStepsBySection(help.sections, matching), [help.sections, matching]);

  // Escape closes only this list; a click outside closes it too. Capture phase, before the tour.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      onClose();
    };
    const onDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target) || anchor?.contains(target)) return;
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('pointerdown', onDown, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('pointerdown', onDown, true);
    };
  }, [anchor, onClose]);

  const r = anchor?.getBoundingClientRect();
  const top = r ? r.bottom + 6 : 80;
  const left = r ? Math.min(Math.max(EDGE, r.left), Math.max(EDGE, window.innerWidth - WIDTH - EDGE)) : EDGE;

  return (
    <div
      ref={ref}
      className="pct-help-jump"
      role="dialog"
      aria-label={s.jumpSearchAria}
      {...{ [HELP_PANEL_ATTR]: 'jump' }}
      {...HELP_TABSTER_ATTRS}
      style={{ top, left }}
    >
      <Input
        autoFocus
        size="small"
        style={{ width: '100%' }}
        contentBefore={<Search16Regular />}
        value={query}
        maxLength={60}
        aria-label={s.jumpSearchAria}
        placeholder={s.jumpPlaceholder}
        onChange={(_, d) => setQuery(d.value)}
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return;
          const first = steps.find((st) => stepMatchesQuery(st, query.trim()));
          if (first) onPick(steps.indexOf(first));
        }}
        data-help="help-jump-search"
      />
      <div className="pct-help-jump-list">
        {groups.length === 0 && <div className="pct-help-jump-note">{s.jumpEmpty}</div>}
        {groups.map((g) => (
          <div key={g.section}>
            <div className="pct-help-jump-group">{localizedSection(help, g.section, language)}</div>
            {g.steps.map((st) => {
              const i = steps.indexOf(st);
              return (
                <button
                  key={st.selector}
                  type="button"
                  className="pct-help-jump-row"
                  aria-current={i === currentIndex ? 'step' : undefined}
                  onClick={() => onPick(i)}
                >
                  <span className="pct-help-jump-row-title">{localizedStep(st, language).title}</span>
                  <span className="pct-help-jump-row-number">{i + 1}</span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
      {query.trim() && matching.length > 0 && (
        <div className="pct-help-jump-note">{fmt(s.jumpFooter, { n: matching.length, m: steps.length })}</div>
      )}
    </div>
  );
};
